import { inject, Injectable } from '@angular/core';
import { Auth, authState } from '@angular/fire/auth';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { orderBy, where } from '@angular/fire/firestore';
import { CallUp, CallUpResponse, CreateCallUpPayload } from '@models';
import { Observable, shareReplay, switchMap, take } from 'rxjs';
import { FirestoreService } from './firestore.service';
import { TenantContextService } from './tenant-context.service';

@Injectable({
  providedIn: 'root',
})
export class CallUpService {
  private readonly firestore = inject(FirestoreService);
  private readonly tenantContext = inject(TenantContextService);
  private readonly auth = inject(Auth);
  private readonly functions = inject(Functions);

  private get col() {
    return this.tenantContext.collections.CALL_UPS;
  }

  byTeam$(teamId: string): Observable<CallUp[]> {
    return this.tenantContext.tenantId$.pipe(
      switchMap(() =>
        this.firestore.listDocuments<CallUp>(
          this.col,
          where('teamId', '==', teamId),
          orderBy('createdAt', 'desc'),
        ),
      ),
      shareReplay(1),
    );
  }

  byMatch$(matchId: string): Observable<CallUp[]> {
    return this.tenantContext.tenantId$.pipe(
      switchMap(() =>
        this.firestore.listDocuments<CallUp>(
          this.col,
          where('matchId', '==', matchId),
        ),
      ),
      shareReplay(1),
    );
  }

  getResponses$(callUpId: string): Observable<CallUpResponse[]> {
    return this.tenantContext.tenantId$.pipe(
      switchMap(() =>
        this.firestore.listDocuments<CallUpResponse>(
          `${this.col}/${callUpId}/responses`,
        ),
      ),
      shareReplay(1),
    );
  }

  async create(payload: CreateCallUpPayload): Promise<string> {
    return this.firestore.create<CallUp>(this.col, {
      ...payload,
      status: 'DRAFT' as const,
    });
  }

  async publish(callUpId: string): Promise<void> {
    const callUp = await new Promise<CallUp>((resolve, reject) => {
      this.firestore
        .getDocument<CallUp>(this.col, callUpId)
        .pipe(take(1))
        .subscribe({ next: resolve, error: reject });
    });
    await this.firestore.update<CallUp>(this.col, {
      id: callUpId,
      status: 'PUBLISHED',
      publishedAt: new Date(),
    });
    try {
      const callable = httpsCallable(
        this.functions,
        'adminSendPushNotification',
      );
      await callable({
        playerIds: callUp.playerIds,
        type: 'call_up_published',
        callUpId,
      });
    } catch {
      // Non-blocking — call-up is published even if notification fails
    }
  }

  myCallUps$(userId: string): Observable<CallUp[]> {
    return this.tenantContext.tenantId$.pipe(
      switchMap(() =>
        this.firestore.listDocuments<CallUp>(
          this.col,
          where('playerIds', 'array-contains', userId),
          where('status', '==', 'PUBLISHED'),
          orderBy('createdAt', 'desc'),
        ),
      ),
      shareReplay(1),
    );
  }

  async confirmAttendance(callUpId: string, confirmed: boolean): Promise<void> {
    const user = await new Promise<{ uid: string } | null>((resolve) => {
      authState(this.auth).pipe(take(1)).subscribe(resolve);
    });
    if (!user) throw new Error('Unauthenticated');
    const responsePath = `${this.col}/${callUpId}/responses`;
    return this.firestore.createOrUpdate<CallUpResponse>(
      responsePath,
      user.uid,
      {
        id: user.uid,
        callUpId,
        userId: user.uid,
        confirmed,
        respondedAt: new Date(),
      },
    );
  }
}
